import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import orderBy from 'lodash/orderBy'

import {
  UserPlanName,
  UserSwitchPlanResponse,
  UserSubscription,
  UserPlan,
  UserPlanUnit,
  UserSubscriptionStatus,
} from '@types'

import api from '@/store/api'

import { prepareResponseError } from '../utils/helpers'

export const useUserSubscriptionsStore = defineStore(
  'user-subscriptions',
  () => {
    // INIT
    const loading = ref(false)

    const currentSubscription = ref<UserSubscription>()

    const plansList = ref<UserPlan[]>()

    let abortController = new AbortController()

    // GETTERS
    const getPlansList = computed(() => {
      if (!plansList.value) {
        fetchPlansList()
      }
      return orderBy(
        plansList.value?.filter(
          plan => plan.name !== UserPlanName.COLLABORATOR,
        ) || [],
        (plan: UserPlan) => plan.plan_schema.id,
      )
    })

    const getCurrentSubscription = computed(() => currentSubscription.value)

    const isInactive = computed(() => {
      if (!getCurrentSubscription.value?.status) return true
      return [
        UserSubscriptionStatus.PAUSED,
        UserSubscriptionStatus.CANCELLED,
        UserSubscriptionStatus.INACTIVE,
      ].includes(getCurrentSubscription.value?.status)
    })

    const getCurrentPlan = computed(() => getCurrentSubscription.value?.plan || {
      name: UserPlanName.STANDARD,
      description: '',
      price: 0,
    } as Partial<UserPlan>)

    const getCurrentPlanSchema = computed(
      () => getCurrentSubscription.value?.plan.plan_schema,
    )

    const getMaxReposNumber = computed(
      () => getCurrentPlanSchema.value?.max_repos_number || 1,
    )

    const getFeatures = computed(
      () => {
        if (getCurrentPlanIndex.value === undefined) return []
        return getActualPlans.value?.[getCurrentPlanIndex.value]?.plan_schema?.features || []
      }
    )

    // ---

    const getActualPlans = computed(() => {
      if (!getCurrentPlanSchema.value) return
      return getPlansList.value?.filter(
        plan => plan.period_unit === getCurrentPlan.value.period_unit,
      )
    })

    const getCurrentPlanIndex = computed(() => {
      if (!getCurrentPlanSchema.value) return
      return (
        getActualPlans.value?.findIndex(
          plan => plan.name === getCurrentPlanSchema.value?.name,
        ) || 0
      )
    })

    const getPreviousPlan = computed(() => {
      if (getCurrentPlanIndex.value === undefined) return
      return getActualPlans.value?.[getCurrentPlanIndex.value - 1]
    })

    const getNextPlan = computed(() => {
      if (getCurrentPlanIndex.value === undefined) return
      return getActualPlans.value?.[getCurrentPlanIndex.value + 1]
    })

    // ACTIONS
    const fetchCurrentSubscription = async () => {
      loading.value = true
      try {
        const result = await api.get('subscription', {
          signal: abortController.signal,
        })
        currentSubscription.value = result.data
      } catch (e) {
        throw Error(prepareResponseError(e))
      } finally {
        loading.value = false
      }
    }

    const fetchHostedPageSession = async () => {
      try {
        const result = await api.get('subscription/hosted_page_session')
        return result.data
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const fetchPortalSession = async () => {
      try {
        const result = await api.get('subscription/portal_session')
        return result.data
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const fetchPlansList = async () => {
      const plansUrl = new URL(
        '@/../public/plans-list.json',
        import.meta.url,
      ).href
      const data = await fetch(plansUrl).then(res => res.json())
      plansList.value = data
    }

    const changePlan = async (
      plan_id: number,
      period_unit: UserPlanUnit,
      deactivated_repositories?: string[],
    ) => {
      try {
        const result = await api.post<UserSwitchPlanResponse>(
          'subscription/switch_plan',
          {
            plan_id,
            period_unit,
            deactivated_repositories,
          },
        )
        return result.data
      } catch (e) {
        throw Error(prepareResponseError(e))
      }
    }

    const cancel = () => {
      abortController.abort()
      abortController = new AbortController()
    }

    const clear = () => {
      currentSubscription.value = undefined
    }

    return {
      loading,

      fetchCurrentSubscription,
      fetchHostedPageSession,
      fetchPortalSession,
      changePlan,

      getPlansList,

      getCurrentSubscription,
      getCurrentPlan,
      getPreviousPlan,
      getNextPlan,

      getMaxReposNumber,
      getFeatures,

      isInactive,

      cancel,
      clear,
    }
  },
)
